/** Partilhar com pessoas: o estado partilhado pela interface.
 *
 *  Três pequenos stores fora do zustand da aplicação, como o dos modelos
 *  partilhados (core/modelos/modelosPartilhados.ts):
 *
 *  - a janela "Partilhar com..." (uma de cada vez, aberta de qualquer
 *    sítio: barra lateral, aviso, painel do BCF, banco dos conflitos);
 *  - "Partilhados comigo" (a lista, lida quando há quem a mostre);
 *  - uma abertura pendente: a página "Partilhados comigo" pede para
 *    abrir uma coisa que só o viewer (ou o banco dos conflitos) sabe
 *    abrir, e quem sabe consome o pedido quando estiver pronto.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { partilhasApi } from './partilhasApi'
import type { ItemComigo, TipoPartilha } from './partilhasRegras'

function criarStore<T>(inicial: T) {
  let atual = inicial
  const ouvintes = new Set<() => void>()
  return {
    ler: () => atual,
    mudar: (v: T) => {
      atual = v
      for (const f of ouvintes) f()
    },
    subscrever: (f: () => void) => {
      ouvintes.add(f)
      return () => {
        ouvintes.delete(f)
      }
    },
  }
}

// -- a janela ----------------------------------------------------------------

/** O que a janela passa a quem cria o recurso: os campos que pediu, e
 *  onde dizer o progresso e os avisos (uma sessão envia modelos antes de
 *  ficar partilhada, e isso leva tempo). */
export interface OpcoesDaCriacao {
  nome?: string
  codigoTriagem?: string
  progresso: (texto: string | null) => void
  aviso: (texto: string) => void
}

export interface PedidoDeJanela {
  tipo: TipoPartilha
  nome: string
  /** null: o recurso ainda não está partilhado (ver `criar`). */
  partilhaId: string | null
  /** Para um recurso ainda por partilhar: partilha-o (com ou sem o
   *  workspace inteiro) e devolve o id da partilha. */
  criar?: (workspaceInteiro: boolean, opcoes: OpcoesDaCriacao) => Promise<string>
  /** Campos a pedir antes de partilhar (a sessão: o nome e, se a
   *  triagem ainda não estiver partilhada, o código dela). Um campo
   *  ausente não aparece. */
  campos?: { nome?: string; codigoTriagem?: string }
  /** O que vai ser partilhado, numa frase (a sessão diz o que inclui). */
  descricao?: string
  /** Para quem gere um recurso já partilhado que se pode refazer (a
   *  sessão: enviar o que mudou desde que foi partilhada). */
  atualizar?: (opcoes: OpcoesDaCriacao) => Promise<void>
  /** O nome do workspace, para a opção "Todo o workspace". */
  workspaceNome?: string
  /** Depois de cada mudança, para a lista de quem abriu se refrescar. */
  aoMudar?: () => void
}

const janela = criarStore<PedidoDeJanela | null>(null)

export function abrirJanelaPartilhar(p: PedidoDeJanela): void {
  janela.mudar(p)
}

export function fecharJanelaPartilhar(): void {
  janela.mudar(null)
}

/** Fora do React (e nos testes). */
export function janelaAgora(): PedidoDeJanela | null {
  return janela.ler()
}

export function useJanelaPartilhar(): PedidoDeJanela | null {
  return useSyncExternalStore(janela.subscrever, janela.ler, janela.ler)
}

// -- partilhados comigo ---------------------------------------------------------

export interface EstadoComigo {
  lista: ItemComigo[] | null
  erro: string | null
}

const comigo = criarStore<EstadoComigo>({ lista: null, erro: null })
let pedido: Promise<void> | null = null

export function recarregarComigo(): Promise<void> {
  if (pedido) return pedido
  pedido = (async () => {
    try {
      comigo.mudar({ lista: await partilhasApi.comigo(), erro: null })
    } catch (err) {
      comigo.mudar({
        ...comigo.ler(),
        erro: err instanceof Error ? err.message : 'Não foi possível ler as partilhas.',
      })
    } finally {
      pedido = null
    }
  })()
  return pedido
}

/** Fora do React (e nos testes). */
export function comigoAgora(): EstadoComigo {
  return comigo.ler()
}

/** A lista "Partilhados comigo". Montar o hook com `ligado` lê-a. */
export function useComigo(ligado: boolean): EstadoComigo {
  const estado = useSyncExternalStore(comigo.subscrever, comigo.ler, comigo.ler)
  useEffect(() => {
    if (ligado) void recarregarComigo()
  }, [ligado])
  return estado
}

/** Para testes e para quem sai da sessão. */
export function esquecerComigo(): void {
  comigo.mudar({ lista: null, erro: null })
}

// -- abertura pendente -------------------------------------------------------------

const abertura = criarStore<ItemComigo | null>(null)

export function pedirAbertura(item: ItemComigo): void {
  abertura.mudar(item)
}

/** Fica com o pedido, se for deste tipo (e tira-o, para não abrir duas
 *  vezes). */
export function tirarAbertura(tipo: TipoPartilha): ItemComigo | null {
  const p = abertura.ler()
  if (!p || p.tipo !== tipo) return null
  abertura.mudar(null)
  return p
}

export function useAberturaPendente(): ItemComigo | null {
  return useSyncExternalStore(abertura.subscrever, abertura.ler, abertura.ler)
}
